import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { StickyNote, Mail, Phone, ListChecks, CalendarPlus } from 'lucide-react'
import { Modal } from './Modal'
import { TaskModal } from './TaskModal'
import { Field, TextInput, TextArea } from './Field'
import { Button } from './Primitives'
import { useStore } from '@/store/useStore'
import { useCurrentUser } from '@/auth/useCurrentUser'
import type { Activity, Contact, EntityRef } from '@/types/entities'

/** A person the Email / Call / Meeting menus can target. */
export interface ActionContact {
  id: string
  name: string
  email?: string
  phone?: string
}

export type QuickAction = 'note' | 'email' | 'call' | 'task' | 'meeting'

/** Contact entity → menu target (work channel first, personal fallback). */
export function toActionContacts(list: Contact[]): ActionContact[] {
  return list.map((c) => ({
    id: c.id,
    name: `${c.firstName} ${c.lastName}`.trim(),
    email: c.workEmail ?? c.personalEmail,
    phone: c.cellPhone ?? c.officePhone,
  }))
}

const newId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

const cleanTel = (raw: string) => raw.replace(/[^+\d]/g, '')

/** Outlook-web meeting compose, pre-addressed. The signed-in M365 session
 *  carries over; executing through M365 is the spec §6 model. */
const meetingUrl = (emails: string[], subject: string) =>
  `https://outlook.office.com/calendar/deeplink/compose?to=${encodeURIComponent(emails.join(';'))}&subject=${encodeURIComponent(subject)}`

/**
 * Record quick-action bar (spec §6) — initiate; execute through M365.
 *
 * Was a row of dead buttons in the prototype. Now:
 *   Email / Meeting — contact picker (incl. "All contacts") → mailto: /
 *                     Outlook-web meeting compose.
 *   Call            — contact picker → tel: (click-to-call handler).
 *   Note / Task     — small create modals; the Activity / ActionItem lands
 *                     on `attachTo` and shows in History / Action Items.
 *                     (In-memory until the CRUD persistence slice.)
 *   More            — removed (no defined behavior).
 */
export function QuickActionBar({
  contacts = [],
  attachTo,
  contextLabel,
  tel,
}: {
  contacts?: ActionContact[]
  /** Enables Note + Task creation against this record. */
  attachTo?: EntityRef
  /** Record name — used in meeting subjects and modal titles. */
  contextLabel?: string
  /** Extra "Main line" entry for the Call menu (e.g. account HQ phone). */
  tel?: string
}) {
  const upsert = useStore((s) => s.upsert)
  const user = useCurrentUser()
  const [openMenu, setOpenMenu] = useState<QuickAction | null>(null)
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null)
  const [modal, setModal] = useState<'note' | 'task' | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!openMenu) return
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return
      setOpenMenu(null)
    }
    // Fixed-positioned portal menu — page scroll/resize invalidates the
    // anchor. Scrolling INSIDE the menu must NOT close it (it did — the
    // capture-phase listener caught the menu's own scrollbar).
    const onMove = (e: Event) => {
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) return
      setOpenMenu(null)
    }
    window.addEventListener('mousedown', onClick)
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('mousedown', onClick)
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [openMenu])

  const emailable = contacts.filter((c) => c.email)
  const callable: ActionContact[] = [
    ...(tel ? [{ id: '__main', name: 'Main line', phone: tel }] : []),
    ...contacts.filter((c) => c.phone),
  ]

  const MENU_WIDTH = 260
  const toggle = (a: QuickAction) => (e: React.MouseEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    setAnchor({
      top: r.bottom + 6,
      left: Math.max(8, Math.min(r.left, window.innerWidth - MENU_WIDTH - 8)),
    })
    setOpenMenu((cur) => (cur === a ? null : a))
  }

  // Rendered through a portal: record heroes crop their children
  // (overflow:hidden for the watermark), which was cutting these off.
  const menu = (items: { key: string; label: string; sub?: string; onPick: () => void }[], empty: string) =>
    anchor && createPortal(
      <div className="qab-menu" role="menu" ref={menuRef} style={{ top: anchor.top, left: anchor.left, width: MENU_WIDTH }}>
        {items.length === 0 && <div className="qab-menu-empty dim">{empty}</div>}
        {items.map((it) => (
          <button key={it.key} className="qab-menu-item" role="menuitem" onClick={() => { setOpenMenu(null); it.onPick() }}>
            <span className="qab-menu-name">{it.label}</span>
            {it.sub && <span className="qab-menu-sub dim">{it.sub}</span>}
          </button>
        ))}
      </div>,
      document.body,
    )

  return (
    <div className="qab" ref={wrapRef}>
      {attachTo && (
        <button className="qab-btn" onClick={() => { setOpenMenu(null); setModal('note') }}>
          <StickyNote size={15} /><span>Note</span>
        </button>
      )}

      <div className="qab-wrap">
        <button className="qab-btn" aria-haspopup="menu" aria-expanded={openMenu === 'email'} onClick={toggle('email')}>
          <Mail size={15} /><span>Email</span>
        </button>
        {openMenu === 'email' && menu(
          [
            ...(emailable.length > 1 ? [{
              key: '__all',
              label: `All contacts (${emailable.length})`,
              onPick: () => { window.location.href = `mailto:${emailable.map((c) => c.email).join(',')}` },
            }] : []),
            ...emailable.map((c) => ({
              key: c.id, label: c.name, sub: c.email,
              onPick: () => { window.location.href = `mailto:${c.email}` },
            })),
          ],
          'No contacts with an email on record.',
        )}
      </div>

      <div className="qab-wrap">
        <button className="qab-btn" aria-haspopup="menu" aria-expanded={openMenu === 'call'} onClick={toggle('call')}>
          <Phone size={15} /><span>Call</span>
        </button>
        {openMenu === 'call' && menu(
          callable.map((c) => ({
            key: c.id, label: c.name, sub: c.phone,
            onPick: () => { window.location.href = `tel:${cleanTel(c.phone!)}` },
          })),
          'No contacts with a phone number on record.',
        )}
      </div>

      {attachTo && (
        <button className="qab-btn" onClick={() => { setOpenMenu(null); setModal('task') }}>
          <ListChecks size={15} /><span>Task</span>
        </button>
      )}

      <div className="qab-wrap">
        <button className="qab-btn" aria-haspopup="menu" aria-expanded={openMenu === 'meeting'} onClick={toggle('meeting')}>
          <CalendarPlus size={15} /><span>Meeting</span>
        </button>
        {openMenu === 'meeting' && menu(
          [
            ...(emailable.length > 1 ? [{
              key: '__all',
              label: `All contacts (${emailable.length})`,
              onPick: () => window.open(meetingUrl(emailable.map((c) => c.email!), `Meeting — ${contextLabel ?? 'WPI'}`), '_blank', 'noopener'),
            }] : []),
            ...emailable.map((c) => ({
              key: c.id, label: c.name, sub: c.email,
              onPick: () => window.open(meetingUrl([c.email!], `Meeting — ${contextLabel ?? 'WPI'}`), '_blank', 'noopener'),
            })),
          ],
          'No contacts with an email on record.',
        )}
      </div>

      {attachTo && (
        <>
          <NoteModal
            open={modal === 'note'}
            onClose={() => setModal(null)}
            onSave={(title, body) => {
              const activity: Activity = {
                id: newId('act'),
                type: 'Note',
                title: title || `Note on ${contextLabel ?? attachTo.label ?? 'record'}`,
                body: body || undefined,
                at: new Date().toISOString(),
                actor: user.displayName,
                attachedTo: [attachTo],
              }
              upsert('activities', activity)
              setModal(null)
            }}
            contextLabel={contextLabel}
          />
          <TaskModal
            open={modal === 'task'}
            onClose={() => setModal(null)}
            attachTo={attachTo}
            contextLabel={contextLabel}
          />
        </>
      )}
    </div>
  )
}

function NoteModal({
  open, onClose, onSave, contextLabel,
}: { open: boolean; onClose: () => void; onSave: (title: string, body: string) => void; contextLabel?: string }) {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  useEffect(() => { if (open) { setTitle(''); setBody('') } }, [open])
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<>Note{contextLabel ? <> — {contextLabel}</> : null}</>}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!title.trim() && !body.trim()} onClick={() => onSave(title.trim(), body.trim())}>
            Save note
          </Button>
        </>
      }
    >
      <Field label="Title">
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short headline" autoFocus />
      </Field>
      <Field label="Note" required>
        <TextArea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="What happened?" />
      </Field>
    </Modal>
  )
}

